import React, { useCallback, useState } from 'react';

import { post } from '@rails/request.js';

import { apiRoute } from '../../lib/api';
import { logError } from '../../lib/logger';

import { SETTINGS_MODAL_CLOSE_BUTTON_CLASS, SETTINGS_MODAL_CLOSE_ICON, SETTINGS_MODAL_TITLE_CLASS } from '../shared/settingsModalChrome';
import { Modal } from './Modal';
import { ModalBody } from './ModalBody';
import { ModalContent } from './ModalContent';
import { ModalTopBar } from './ModalTopBar';

// The server forces kind: open_breakout on this endpoint, so every channel
// created here is a breakout of the workspace's main channel.
const NewChannelModal = ({ onClose, onChannelCreated }) => {
  const [name, setName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);

  const trimmedName = name.trim();

  const handleNameChange = useCallback((event) => {
    setName(event.target.value);
    setError(null);
  }, []);

  const handleSubmit = useCallback(
    async function handleSubmitCallback(event) {
      event.preventDefault();
      if (!trimmedName || isCreating) return;

      setIsCreating(true);
      setError(null);

      try {
        const response = await post(apiRoute('/v1/channels'), {
          body: { channel: { name: trimmedName } },
        });
        const data = await response.json;

        if (data.ok) {
          onChannelCreated(data.data.channel);
        } else {
          const err = data.errors?.name || data.errors?.base || data.errors?.general;
          setError(Array.isArray(err) ? err[0] : err || 'Please try again.');
        }
      } catch (requestError) {
        logError('NewChannelModal', 'Could not create channel', { error: requestError });
        setError('An error occurred. Please try again.');
      } finally {
        setIsCreating(false);
      }
    },
    [trimmedName, isCreating, onChannelCreated],
  );

  return (
    <Modal
      onClose={onClose}
      className="rounded-2xl"
      closePlacement="top-left"
      closeButtonClassName={SETTINGS_MODAL_CLOSE_BUTTON_CLASS}
      closeIcon={SETTINGS_MODAL_CLOSE_ICON}
    >
      <ModalBody className="h-full">
        <ModalTopBar className="!pb-0">
          <span className={SETTINGS_MODAL_TITLE_CLASS}>New Channel</span>
        </ModalTopBar>
        <ModalContent className="min-h-0 md:!px-5">
          <form onSubmit={handleSubmit} className="w-full pt-6">
            <div>
              <input
                type="text"
                id="channel_name"
                name="channel_name"
                value={name}
                onChange={handleNameChange}
                placeholder="Channel name"
                aria-label="Channel name"
                autoFocus
                maxLength={80}
                autoComplete="off"
                data-1p-ignore="true"
                className="h-12 w-full rounded-lg border border-white bg-gray-50 px-4 text-[15px] leading-5 text-black placeholder:text-gray-400 hover:bg-white focus:outline-none sm:border-[0.5px] dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100 dark:placeholder:text-neutral-500"
              />
            </div>
            {error && <span className="mt-1 block text-left text-xs text-red-400">{error}</span>}

            <button
              type="submit"
              data-app-region="none"
              disabled={!trimmedName || isCreating}
              className="mt-4 flex h-12 w-full cursor-pointer items-center justify-center rounded-full bg-idle-blue text-[15px] leading-5 font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 dark:disabled:bg-neutral-800 dark:disabled:text-neutral-500"
            >
              {isCreating ? 'Creating…' : 'Create channel'}
            </button>
          </form>
        </ModalContent>
      </ModalBody>
    </Modal>
  );
};

export default NewChannelModal;
